import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpErrorResponse,
} from "@angular/common/http";
import { Observable } from "rxjs";
import { IKeystoneAuthStorage } from "./keystone-auth.storage";
import { switchMap, tap } from "rxjs/operators";
import moment from "moment";
import { environment } from "../app/environments/default";

interface Options {
  body?: any;
  headers?: HttpHeaders;
  observe: string;
  params?:
    | HttpParams
    | {
        [param: string]: string | string[];
      };
  reportProgress?: boolean;
  responseType: string;
  withCredentials?: boolean;
}

@Injectable()
export class KeystoneHttpClient {
  public baseUrl: string;
  constructor(
    private http: HttpClient,
    private authStorage: IKeystoneAuthStorage
  ) {
    this.baseUrl = environment.keystoneHttpClient.baseUrl;
  }

  public retrieveHeaders(tryRefresh = true): Observable<any> {
    const getToken$: Observable<any> = Observable.fromPromise(
      this.authStorage.getToken()
    );
    const getUid$: Observable<any> = Observable.fromPromise(
      this.authStorage.getUid()
    );
    const getTokenExpiration$: Observable<any> = Observable.fromPromise(
      this.authStorage.getTokenExpiration()
    );
    const getTokenRefresh$: Observable<any> = Observable.fromPromise(
      this.authStorage.getRefreshToken()
    );
    return Observable.combineLatest(
      getToken$,
      getUid$,
      getTokenExpiration$,
      getTokenRefresh$
    ).pipe(
      switchMap(([token, uid, tokenExpiration, refreshToken]) => {
        /**
         * check if auth informations exists
         */
        if (!token || !uid || !tokenExpiration || !refreshToken) {
          throw new HttpErrorResponse({
            error: {
              message: "Login Information Missing",
              code: "MISSING_AUTH",
            },
            status: 401,
          });
        }
        /**
         * check if token is espiring
         */
        const diff = moment(tokenExpiration).diff(moment(), "h");
        /**
         * force refresh-token if the diff is less then 4
         */
        if (diff <= 4 && tryRefresh) {
          return this.post("refresh-token", { refreshToken }, null, false).map(
            (user) => {
              uid = user._id;
              this.authStorage.setUid(uid);
              token = user.apiToken;
              this.authStorage.setToken(token);
              tokenExpiration = user.apiTokenExpiration;
              this.authStorage.setTokenExpiration(tokenExpiration);
              refreshToken = user.apiRefreshToken;
              this.authStorage.setRefreshToken(refreshToken);
              return [token, uid, tokenExpiration, refreshToken];
            }
          );
        }
        return Observable.of([token, uid, tokenExpiration, refreshToken]);
      })
    );
  }

  private authRequest(
    method,
    url,
    body,
    options = {},
    tryRefresh = true,
    auth = true
  ) {
    /**
     * Add / if is missing in the baseUrl
     */
    const _baseUrl =
      this.baseUrl + (this.baseUrl[this.baseUrl.length - 1] === "/" ? "" : "/");
    /**
     * append resource to baseUrl
     */
    const _url = _baseUrl + url;

    /**
     * setup Options object
     */

    const _options: Options = {} as Options;
    if (body) _options.body = body;
    let headers = new HttpHeaders();
    if (body instanceof FormData) {
      headers = headers.append("enctype", "multipart/form-data");
      headers = headers.append("Content-Type", "multipart/form-data");
    } else {
      headers = headers.append("Content-Type", "application/json");
    }
    _options.responseType = "json";

    // if(tryRefresh){
    if (auth) {
      return this.retrieveHeaders(tryRefresh).pipe(
        switchMap(([token, uid, tokenExpiration, refreshToken]) => {
          headers = headers.append("token", token);
          headers = headers.append("uid", uid);
          _options.headers = headers;
          return this.http.request(method, _url, {
            ..._options,
            ...options,
          } as any) as Observable<any>;
        })
      );
    } else {
      return this.http.request(method, _url, {
        ..._options,
        ...options,
      } as any) as Observable<any>;
    }
  }

  getBaseUrl() {
    return this.baseUrl;
  }

  get(url, options = {}, auth = true) {
    return this.authRequest("GET", url, null, options, true, auth);
  }

  post(url, body = {}, options = {}, tryRefresh = true, auth = true) {
    return this.authRequest("POST", url, body, options, tryRefresh, auth);
  }

  put(url, body = {}, options = {}) {
    return this.authRequest("PUT", url, body, options);
  }

  patch(url, body = {}, options = {}) {
    return this.authRequest("PATCH", url, body, options);
  }

  delete(url, options = {}) {
    return this.authRequest("DELETE", url, null, options);
  }
}



// WEBPACK FOOTER //
// ./src/keystone-http/keystone-http-client.service.ts